import { useState } from 'react'

import { nhost } from '@/shared/lib/nhost'
import { SERVER_URL } from '@/shared/lib/serverUrl'
import { readStorage, VOUCHER_CODE_KEY } from '@/shared/lib/storageKeys'

/**
 * Die Weiterleitung zu Stripe. Die Sitzung legt der Server an, denn nur er kennt den
 * geheimen Schlüssel und darf den Gutschein in einen Preis umrechnen.
 *
 * Es wird nichts zurückgesetzt, wenn es klappt: Der Browser verlässt die Seite, und
 * nach der Zahlung kommt er mit `?payment=success` zurück - siehe `usePaidState`.
 */
export function useStripeCheckout(chapterSlug: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startCheckout = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const successUrl = `${window.location.origin}/user/chapter/${chapterSlug}?payment=success`
      const cancelUrl = window.location.href
      // Aus dem Speicher und nicht aus dem Hook: Der Server prüft den Code noch einmal
      // selbst, und hier soll genau der stehen, den die Nutzerin eingelöst hat.
      const storedVoucher = readStorage(VOUCHER_CODE_KEY)

      const accessToken = nhost.auth.getAccessToken()
      const res = await fetch(`${SERVER_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ successUrl, cancelUrl, voucherCode: storedVoucher }),
      })

      if (!res.ok) {
        const errorText = await res.text()
        throw new Error(`Server error: ${res.status} - ${errorText}`)
      }

      const { url } = await res.json()
      if (!url) throw new Error('Keine Checkout-URL erhalten')

      // Der Gutschein bleibt liegen: Bricht die Zahlung ab, gilt er beim nächsten
      // Versuch weiter. Entfernt wird er erst nach erfolgreicher Zahlung.
      window.location.href = url
    } catch (caught) {
      console.error('Checkout error:', caught)
      setError(caught instanceof Error ? caught.message : 'Fehler beim Checkout')
      setIsLoading(false)
    }
  }

  return { startCheckout, isLoading, error }
}
